import GeneralUtility from '../utilities/general-utility';

/**
 * Represents a status error messsage.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
interface StatusError {
    message: string,
    code: number
}

/**
 * Represents a step status.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
interface StepStatus {
    pageId: number,
    type: string,
    status: number,
    errors: StatusError[]
}

/**
 * Represents a checklist status.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
interface Status {
    title: string,
    stepCount: number,
    totalSteps: number,
    errors: StatusError[],
    steps: { [key: number]: StepStatus }
}

/**
 * Initializes functions related to the checklist pages.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
export default class ChecklistPage {
    /**
     * Main section container of the checklist.
     */
    private readonly section: HTMLElement;

    /**
     * Checklist menu.
     */
    private readonly menu: HTMLElement;

    /**
     * UID of the currently active checklist.
     */
    private readonly uid: number;

    /**
     * Contains the cached checklist status.
     */
    private status: Status | null = null;

    /**
     * Constructor.
     *
     * @param {HTMLElement} section Checklist section
     * @param {HTMLElement} menu Checklist menu
     */
    constructor(section: HTMLElement, menu: HTMLElement) {
        this.section = section;
        this.menu = menu;
        if (!section.dataset.checklist) {
            throw new Error('Required elements missing');
        }
        this.uid = parseInt(section.dataset.checklist);

        // Get the checklist status
        this.fetchStatus().then(() => {
            // this.status is available at this point
            this.initializeMenu();
        });
    }

    /**
     * Fetches the current checklist status.
     *
     * @param {bool} [force] If true, then cached status will be ignored (defaults to false)
     * @returns {Promise} Contains JSON status of the checklist
     */
    private fetchStatus(force: boolean = false): Promise<any> {
        // Returned cached status, if possible
        if (!force && this.status) {
            return new Promise((resolve) => {
                resolve(this.status);
            });
        }

        return fetch(GeneralUtility.getUrl() + '?type=684028&checklist=' + this.uid + '&rand=' + Math.random())
            .then(response => {
                if (response.status !== 200) {
                    throw new Error(response.status + ' ' + response.statusText);
                }
                return response.json();
            })
            .then((json: Status) => {
                this.status = json;
                return this.status;
            })
            .catch(reason => {
                throw new Error('An error occurred while getting the checklist status: ' + reason);
            });
    }

    /**
     * Initializes the checklist menu on the side with status icons.
     */
    private initializeMenu(): void {
        // Validate menu items on the first level
        document.querySelectorAll<HTMLLIElement>('#checklistMenu > li').forEach(item => {
            this.showStatusForMenuItem(item);

            // Initialize submenu items
            const submenu = item.querySelector<HTMLUListElement>('ul');
            const subItems = item.querySelectorAll<HTMLLIElement>('ul > li');
            if (submenu && subItems.length > 0) {
                this.initializeSubmenu(item, submenu, subItems);
            }
        });
    }

    /**
     * Initializes a submenu of a checklist menu item.
     *
     * @param {HTMLLIElement} parentItem Parent menu item
     * @param {HTMLUListElement} submenu Submenu list
     * @param {NodeListOf<HTMLLIElement>} items Submenu items
     */
    private initializeSubmenu(parentItem: HTMLLIElement, submenu: HTMLUListElement, items: NodeListOf<HTMLLIElement>): void {
        // Helper interfaces for the middleware result
        interface Page {
            uid: number,
            title: string
        }

        interface Subpages {
            pages: Page[],
            sorting: number[]
        }

        const status = {
            none: -1,
            success: 0,
            error: 1,
            warning: -2
        };


        const parentLink = parentItem.querySelector<HTMLLinkElement>('a');
        if (!parentLink || !parentItem.dataset.pageUid) {
            return;
        }
        const pageUid = parseInt(parentItem.dataset.pageUid);

        let parentStatus = status.none;
        if (parentLink.classList.contains('success')) {
            parentStatus = status.success;
        } else if (parentLink.classList.contains('error')) {
            parentStatus = status.error;
        }

        // Fetch the enabled submenu items for the sport offer menu item
        fetch(GeneralUtility.getUrl() + '?offer-api[endpoint][]=checklist&offer-api[endpoint][]=subpages&checklist=' + this.uid + '&pageUid=' + pageUid + '&rand=' + Math.random())
            .then(response => {
                if (response.status !== 200) {
                    throw new Error(response.status + ' ' + response.statusText);
                }
                return response.json();
            })
            .then((subpages: Subpages) => {
                // Remember the amount of errors and success codes, so that the parent element can be adjusted accordingly
                let level1ErrorCount = 0;
                let level1SuccessCount = 0;

                // Enable submenu items
                let enabledCount = 0;
                for (const pageUid of subpages.sorting) {
                    // Filter out the menu item for this page UID
                    const filtered = [...items].filter(subItem => {
                        if (!subItem.dataset.pageUid) {
                            return false;
                        }
                        return parseInt(subItem.dataset.pageUid) === pageUid;
                    });

                    if (filtered.length === 1) {
                        const item = filtered[0];
                        const linkText = item.querySelector<HTMLSpanElement>('a span.text');
                        if (!linkText) {
                            return;
                        }
                        const page = subpages.pages[pageUid];

                        // Enable item
                        item.classList.remove('d-none');
                        submenu.append(item); // Append to the bottom, which will lead to the correct sorting in the end
                        enabledCount++;

                        // Add custom menu title from the checklist step
                        linkText.innerText = page.title;

                        // Apply checklist step status
                        const result = this.showStatusForMenuItem(item);
                        if (result === 1) {
                            level1ErrorCount++;
                        } else if (result === 0) {
                            level1SuccessCount++;
                        }
                    }
                }

                // Show submenu
                if (enabledCount > 0) {
                    submenu.classList.remove('d-none');

                    // Adjust status icon of the parent element
                    let submenuStatus = status.none;
                    if (level1ErrorCount > 0 && level1SuccessCount > 0) {
                        submenuStatus = status.warning;
                    } else if (level1ErrorCount > 0) {
                        submenuStatus = status.error;
                    } else if (level1SuccessCount > 0) {
                        submenuStatus = status.success;
                    }

                    if (
                        (parentStatus === status.none || parentStatus === status.success) &&
                        (submenuStatus === status.error || submenuStatus === status.warning)
                    ) {
                        parentLink.classList.remove('warning', 'error', 'success');
                        parentLink.classList.add('warning');
                    }
                }
            });
    }

    /**
     * Shows the checklist step status for a menu item, if a step status for this menu item is present.
     *
     * @param {HTMLLIElement} item Menu item
     * @returns {number} 0 if everything was OK, 1 if there was an error, -1 if checks do not apply
     */
    private showStatusForMenuItem(item: HTMLLIElement): number {
        const link = item.querySelector<HTMLLinkElement>('a');
        if (!this.status || !link || !item.dataset.pageUid) {
            return -1;
        }
        const pageUid = parseInt(item.dataset.pageUid);
        const step = this.status.steps[pageUid];
        if (!step) {
            return -1;
        }

        link.classList.remove('error', 'success');
        link.setAttribute('title', '');

        if (step.type === 'OfferChecklistStep' && step.status < 1) {
            link.classList.add('error');
            return 1;
        } else if (step.errors && step.errors.length > 0) {
            link.classList.add('error');
            link.setAttribute('title', step.errors[0].message);
            return 1;
        }

        link.classList.add('success');
        return 0;
    }
}
